import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { FileDropEntry } from './file-drop-entry';
import { FileSystemDirectoryEntry, FileSystemEntry, FileSystemFileEntry, IFile } from './interfaces';
import { isSafari } from '@app/shared/helpers/environment';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
  selector: 'app-smash-file-drop',
  templateUrl: './smash-file-drop.component.html',
  styleUrls: ['./smash-file-drop.component.scss'],
})
export class SmashFileDropComponent implements OnDestroy {

  @Input() public accept = '*';
  @Input() public directory = false;
  @Input() public multiple = true;
  @Input() public useDragEnter = false;
  @Input() public dropDisabled = false;

  @Output() public fileDrop: EventEmitter<FileDropEntry[]> = new EventEmitter();
  @Output() public fileOver: EventEmitter<any> = new EventEmitter();
  @Output() public fileLeave: EventEmitter<any> = new EventEmitter();
  @Output() public longpressChanged: EventEmitter<any> = new EventEmitter();

  @ViewChild('fileSelector', { static: true }) public fileSelector: ElementRef;

  @ContentChild(TemplateRef) contentTemplate: TemplateRef<any>;

  public isDraggingOverDropZone = false;

  private globalDraggingInProgress = false;
  private readonly globalDragStartListener: () => void;
  private readonly globalDragEndListener: () => void;

  private files: FileDropEntry[] = [];
  private numOfActiveReadEntries = 0;

  private helperFormEl: HTMLFormElement | null = null;
  private fileInputPlaceholderEl: HTMLDivElement | null = null;

  private dropEventTimerSubscription: Subscription | null = null;

  private _disabled = false;
  private _longpress: any;

  public get disabled(): boolean { return this._disabled; }

  @Input()
  public set disabled(value: boolean) {
    this._disabled = (value != null && `${value}` !== 'false');
  }
  public buffer: any = null;
  public loadingFolder = false;
  private endPush: number;
  private startPush: number;

  constructor(
    private zone: NgZone,
    private renderer: Renderer2,
    private deviceDetector: DeviceDetectorService,
  ) {
    this.globalDragStartListener = this.renderer.listen('document', 'dragstart', (evt: Event) => {
      this.globalDraggingInProgress = true;
    });
    this.globalDragEndListener = this.renderer.listen('document', 'dragend', (evt: Event) => {
      this.globalDraggingInProgress = false;
    });
  }

  public ngOnDestroy(): void {
    if (this.dropEventTimerSubscription) {
      this.dropEventTimerSubscription.unsubscribe();
      this.dropEventTimerSubscription = null;
    }
    this.globalDragStartListener();
    this.globalDragEndListener();
    this.files = [];
    this.helperFormEl = null;
    this.fileInputPlaceholderEl = null;
  }

  public onDragOver(event: DragEvent): void {
    if (!this.dropDisabled) {
      if (this.useDragEnter) {
        this.preventAndStop(event);
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'copy';
        }
      } else if (!this.isDropzoneDisabled() && !this.useDragEnter && event.dataTransfer) {
        if (!this.isDraggingOverDropZone) {
          this.isDraggingOverDropZone = true;
          this.fileOver.emit(event);
        }
        this.preventAndStop(event);
        event.dataTransfer.dropEffect = 'copy';
      }
    }
  }

  public onDragEnter(event: Event): void {
    if (!this.dropDisabled) {
      if (!this.isDropzoneDisabled() && this.useDragEnter) {
        if (!this.isDraggingOverDropZone) {
          this.isDraggingOverDropZone = true;
          this.fileOver.emit(event);
        }
        this.preventAndStop(event);
      }
    }
  }

  public onDragLeave(event: any): void {
    if (!this.dropDisabled) {
      if (!this.isDropzoneDisabled()) {
        this.isDraggingOverDropZone = false;
        this.fileLeave.emit(event);
        this.preventAndStop(event);
      }
    }
  }

  public dropFiles(event: DragEvent): void {
    if (!this.isDropzoneDisabled()) {
      this.isDraggingOverDropZone = false;
      if (event.dataTransfer) {
        let items: FileList | DataTransferItemList;
        if (event.dataTransfer.items) {
          items = event.dataTransfer.items;
        } else {
          items = event.dataTransfer.files;
        }
        this.preventAndStop(event);
        this.checkFiles(items);
      }
    }
  }

  public openFileSelector = (event?: MouseEvent): void => {
    if (this.longpress !== null) {
      clearTimeout(this.longpress);
      this.longpress = null;
    }
    if (this.buffer !== null) {
      clearTimeout(this.buffer);
      this.buffer = null;
    }
    if (this.fileSelector?.nativeElement) {
      setTimeout(() => (this.fileSelector.nativeElement as HTMLInputElement).click(), 0);
    }
  }

  public cancelFolderSelector = (event?: MouseEvent): void => {
    if (event.type === 'mouseup' && event.button === 0) {
      this.endPush = (new Date()).getTime();
      const shortpush = this.endPush - this.startPush;
      this.directory = isSafari() && this.deviceDetector.isDesktop() ? true : shortpush > 800;
      if (shortpush < 800) {
        clearTimeout(this.buffer);
        clearTimeout(this.longpress);
        this.buffer = null;
        this.longpress = null;
        this.openFileSelector(event);
      }
    }
  }

  public startFolderSelector = (event?: MouseEvent): void => {
    if (event.button === 0) {
      this.directory = false;
      this.buffer = null;
      this.longpress = null;
      this.startPush = (new Date()).getTime();
      this.buffer = setTimeout(() => this.triggerCharge(event), 200); // buffer time
    }
  }

  triggerCharge(event: MouseEvent): void {
    this.longpress = setTimeout(() => {
      this.directory = true;
      this.openFileSelector(event);
    }, 600);
  }

  /**
   * Processes the change event of the file input and adds the given files.
   * @param Event event
   */
  public uploadFiles(event: Event): void {
    if (!this.isDropzoneDisabled()) {
      if (event.target) {
        const items = (event.target as HTMLInputElement).files || ([] as any);
        this.checkFiles(items);
        this.resetFileInput();
      }
    }
  }

  set longpress(value: any) {
    this._longpress = value;
    this.longpressChanged.emit(value);
  }

  get longpress(): any {
    return this._longpress;
  }

  private checkFiles(items: FileList | DataTransferItemList): void {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      let entry: FileSystemEntry | null = null;
      if (this.canGetAsEntry(item)) {
        entry = item.webkitGetAsEntry();
      }

      if (!entry) {
        if (item) {
          const fakeFileEntry: FileSystemFileEntry = {
            name: (item as File).name,
            isDirectory: false,
            isFile: true,
            file: <T>(callback: (filea: IFile) => T) => callback(item as IFile),
          };
          const toUpload: FileDropEntry = new FileDropEntry(fakeFileEntry.name, fakeFileEntry);
          this.addToQueue(toUpload);
        }
      } else {
        if (entry.isFile) {
          const toUpload: FileDropEntry = new FileDropEntry(entry.name, entry);
          this.addToQueue(toUpload);
        } else if (entry.isDirectory) {
          this.traverseFileTree(entry, entry.name);
        }
      }
    }

    if (this.dropEventTimerSubscription) {
      this.dropEventTimerSubscription.unsubscribe();
    }
    this.dropEventTimerSubscription = timer(200, 200)
      .subscribe(() => {
        if (this.files.length > 0 && this.numOfActiveReadEntries === 0) {
          const files = this.files;
          this.files = [];
          this.fileDrop.emit(files);
        }
      });
  }

  private traverseFileTree(item: FileSystemEntry, path: string): void {
    if (item.isFile) {
      const toUpload: FileDropEntry = new FileDropEntry(path, item);
      this.files.push(toUpload);

    } else {
      path = path + '/';
      const dirReader = (item as FileSystemDirectoryEntry).createReader();
      let entries: FileSystemEntry[] = [];

      const readEntries = () => {
        this.numOfActiveReadEntries++;
        dirReader.readEntries((result) => {
          if (!result.length) {
            // add empty folders
            if (entries.length > 0) {
              for (let i = 0; i < entries.length; i++) {
                this.zone.run(() => {
                  this.traverseFileTree(entries[i], path + entries[i].name);
                });
              }
            }
          } else {
            // continue with the reading
            entries = entries.concat(result);
            readEntries();
          }

          this.numOfActiveReadEntries--;
        });
      };

      readEntries();
    }
  }

  /**
   * Clears any added files from the file input element so the same file can subsequently be added multiple times.
   */
  private resetFileInput(): void {
    if (this.fileSelector && this.fileSelector.nativeElement) {
      const fileInputEl = this.fileSelector.nativeElement as HTMLInputElement;
      const fileInputContainerEl = fileInputEl.parentElement;
      const helperFormEl = this.getHelperFormElement();
      const fileInputPlaceholderEl = this.getFileInputPlaceholderElement();

      // Just a quick check so we do not mess up the DOM (will never happen though).
      if (fileInputContainerEl !== helperFormEl) {
        // Insert the form input placeholder in the DOM before the form input element.
        this.renderer.insertBefore(fileInputContainerEl, fileInputPlaceholderEl, fileInputEl);
        // Add the form input as child of the temporary form element, removing the form input from the DOM.
        this.renderer.appendChild(helperFormEl, fileInputEl);
        // Reset the form, thus clearing the input element of any files.
        helperFormEl.reset();
        // Add the file input back to the DOM in place of the file input placeholder element.
        this.renderer.insertBefore(fileInputContainerEl, fileInputEl, fileInputPlaceholderEl);
        // Remove the input placeholder from the DOM
        this.renderer.removeChild(fileInputContainerEl, fileInputPlaceholderEl);
      }
    }
  }

  /**
   * Get a cached HTML form element as a helper element to clear the file input element.
   */
  private getHelperFormElement(): HTMLFormElement {
    if (!this.helperFormEl) {
      this.helperFormEl = this.renderer.createElement('form') as HTMLFormElement;
    }

    return this.helperFormEl;
  }

  /**
   * Get a cached HTML div element to be used as placeholder for the file input element when clearing said element.
   */
  private getFileInputPlaceholderElement(): HTMLDivElement {
    if (!this.fileInputPlaceholderEl) {
      this.fileInputPlaceholderEl = this.renderer.createElement('div') as HTMLDivElement;
    }

    return this.fileInputPlaceholderEl;
  }

  private canGetAsEntry(item: any): item is DataTransferItem {
    return !!item.webkitGetAsEntry;
  }

  private isDropzoneDisabled(): boolean {
    return (this.globalDraggingInProgress || this.disabled);
  }

  private addToQueue(item: FileDropEntry): void {
    this.files.push(item);
  }

  private preventAndStop(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }
}
